import type {
  NavigationProviderProps,
  BaseAppProps,
} from '@events-helsinki/components';
import {
  useResilientTranslation,
  useLocale,
  BaseApp,
  BaseAppFallbackComponent as FallbackComponent,
  getResilientTranslation,
} from '@events-helsinki/components';
import type { SSRConfig } from 'next-i18next/pages';
import { appWithTranslation } from 'next-i18next/pages';
import React from 'react';

import '../styles/globals.scss';
import '../styles/askem.scss';
import { ErrorBoundary } from 'react-error-boundary';
import nextI18nextConfig from '../../next-i18next.config.mjs';
import { ROUTES } from '../constants';
import AppConfig from '../domain/app/AppConfig';
import cmsHelper from '../domain/app/headlessCmsHelper';
import HobbiesApolloProvider from '../domain/app/HobbiesApolloProvider';
import routerHelper from '../domain/app/routerHelper';
import {
  getCardUrl,
  getEventListLinkUrl,
  getEventUrl,
  getKeywordOnClickHandler,
  getOrganizationSearchUrl,
  getHelsinkiOnlySearchUrl,
  getPlainEventUrl,
} from '../domain/search/eventSearch/utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AppProps<P = any> = Readonly<{
  pageProps: P;
  Component: React.ComponentType<P>;
}>;

export type CustomPageProps = NavigationProviderProps & SSRConfig;

const ErrorBoundaryFallbackComponent = ({ error }: { error: Error }) => {
  const { resilientT } = useResilientTranslation();
  const appName = resilientT('appHobbies:appName');
  return <FallbackComponent error={error} appName={appName} />;
};

function MyApp({ Component, pageProps }: AppProps<CustomPageProps>) {
  const locale = useLocale();
  const appName: BaseAppProps['appName'] = {
    en: getResilientTranslation('appHobbies:appName', 'en'),
    fi: getResilientTranslation('appHobbies:appName', 'fi'),
    sv: getResilientTranslation('appHobbies:appName', 'sv'),
  };
  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallbackComponent}>
      <HobbiesApolloProvider>
        <BaseApp
          appName={appName}
          globalAppName="Hobbies"
          cmsHelper={cmsHelper}
          cookieDomain={AppConfig.hostname}
          routerHelper={routerHelper}
          matomoConfiguration={AppConfig.matomoConfiguration}
          askemFeedbackConfiguration={AppConfig.askemFeedbackConfiguration(
            locale
          )}
          consentUrl={ROUTES.COOKIE_CONSENT}
          defaultButtonTheme={AppConfig.defaultButtonTheme}
          defaultButtonVariant={AppConfig.defaultButtonVariant}
          getCardUrl={getCardUrl}
          getEventUrl={getEventUrl}
          getEventListLinkUrl={getEventListLinkUrl}
          getOrganizationSearchUrl={getOrganizationSearchUrl}
          getHelsinkiOnlySearchUrl={getHelsinkiOnlySearchUrl}
          getPlainEventUrl={getPlainEventUrl}
          getKeywordOnClickHandler={getKeywordOnClickHandler}
          {...pageProps}
        >
          <Component {...pageProps} />
        </BaseApp>
      </HobbiesApolloProvider>
    </ErrorBoundary>
  );
}

export default appWithTranslation(MyApp, {
  ...nextI18nextConfig,
});
